import { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { HiChevronLeft } from 'react-icons/hi';

import { Modal, ModalMethods, Button, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import type { StitchOptionApi } from './optionsModalTypes';

type NumberOptionModalProps = {
  atomicOutfitId: number | null;
  existingOption: StitchOptionApi | null;
  onSaved: () => void;
  onClose: () => void;
};

const NumberOptionModal = forwardRef<ModalMethods, NumberOptionModalProps>(
  ({ atomicOutfitId, existingOption, onSaved, onClose }, ref) => {
    const modalRef = useRef<ModalMethods>(null);
    const [fieldLabel, setFieldLabel] = useState('');
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(5);
    const [defaultValue, setDefaultValue] = useState<number>(0);
    const [price, setPrice] = useState<number>(0);
    const [toBeMultiplied, setToBeMultiplied] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useImperativeHandle(ref, () => ({
      show: () => modalRef.current?.show(),
      hide: () => modalRef.current?.hide(),
      toggle: () => modalRef.current?.toggle(),
      getModalState: () => modalRef.current?.getModalState() ?? false,
    }));

    useEffect(() => {
      if (existingOption) {
        setFieldLabel(existingOption.key);
        setMin(existingOption.min_value ?? 0);
        setMax(existingOption.max_value ?? 10);
        setDefaultValue(existingOption.default_value ?? existingOption.min_value ?? 0);
        const firstValue = existingOption.values?.[0];
        setPrice(firstValue ? Number(firstValue.price) || 0 : 0);
        setToBeMultiplied(false);
      } else {
        setFieldLabel('');
        setMin(0);
        setMax(5);
        setDefaultValue(0);
        setPrice(0);
        setToBeMultiplied(false);
      }
    }, [existingOption]);

    const handleSave = async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !atomicOutfitId) {
        toasts('error', 'Boutique or piece not selected', 'number-option-save-error');

        return;
      }

      const trimmedLabel = fieldLabel.trim();

      if (!trimmedLabel) {
        toasts('error', 'Enter field label', 'number-option-label-error');

        return;
      }

      setIsSaving(true);
      try {
        const payload = {
          atomic_outfit_id: atomicOutfitId,
          number_stitch_option_request: {
            field_label: trimmedLabel,
            field_type: 2,
            min: Number(min) ?? 0,
            max: Number(max) ?? 0,
            default_value: Number(defaultValue) ?? 0,
            price: Number(price) ?? 0,
            to_be_multiplied: toBeMultiplied,
          },
        };

        if (existingOption) {
          const response = await api.putRequest(
            `new-stitch-option/stitch-options/${existingOption.id}/v2`,
            payload
          );

          if (response.status) {
            toasts('success', 'Option updated successfully', 'number-option-update-success');
            modalRef.current?.hide();
            onSaved();
          } else {
            toasts(
              'error',
              response.message || 'Failed to update option',
              'number-option-update-error'
            );
          }
        } else {
          const response = await api.postRequest(
            `new-stitch-option/${boutique_id}/boutique_stitch_options`,
            payload
          );

          if (response.status) {
            toasts('success', 'Option added successfully', 'number-option-add-success');
            modalRef.current?.hide();
            onSaved();
          } else {
            toasts('error', response.message || 'Failed to add option', 'number-option-add-error');
          }
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'number-option-save-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const headerComponent = (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            modalRef.current?.hide();
            onClose();
          }}
          className="p-1 rounded hover:bg-gray-100 flex items-center gap-1"
          aria-label="Back"
        >
          <HiChevronLeft size={24} />
          <span className="font-inter text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-lg border-2 border-blue-500 flex items-center justify-center font-semibold text-blue-600 text-sm">
            123
          </span>
          <span className="font-inter font-semibold text-lg">Input number field</span>
        </div>
      </div>
    );

    const footerComponent = (
      <Button
        label="Save"
        size="medium"
        bgColor="primary"
        onClick={handleSave}
        disabled={isSaving}
        className="flex items-center gap-2"
      />
    );

    return (
      <Modal
        ref={modalRef}
        size="xl"
        headerComponent={headerComponent}
        footerComponent={footerComponent}
        showFooter={true}
        showCloseIcon={false}
      >
        <div className="flex flex-col gap-4 py-4">
          <div>
            <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
              Field label
            </label>
            <input
              type="text"
              value={fieldLabel}
              onChange={(e) => setFieldLabel(e.target.value)}
              placeholder="Enter field label"
              className="w-full font-inter border-b border-gray-300 py-2 px-0 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div>
              <label className="font-inter text-sm font-medium text-gray-700 block mb-1">Min</label>
              <input
                type="number"
                value={min}
                onChange={(e) => setMin(Number(e.target.value) || 0)}
                className="w-20 font-inter border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <span className="self-end pb-2">-</span>
            <div>
              <label className="font-inter text-sm font-medium text-gray-700 block mb-1">Max</label>
              <input
                type="number"
                value={max}
                onChange={(e) => setMax(Number(e.target.value) || 0)}
                className="w-20 font-inter border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
                ₹ Price
              </label>
              <input
                type="number"
                value={price === 0 ? '' : price}
                onChange={(e) => setPrice(e.target.value ? Number(e.target.value) : 0)}
                className="w-24 font-inter border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
              Default Value
            </label>
            <input
              type="number"
              value={defaultValue}
              onChange={(e) => setDefaultValue(Number(e.target.value) || 0)}
              className="w-32 font-inter border border-gray-300 rounded px-2 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="number-to-be-multiplied"
              checked={toBeMultiplied}
              onChange={(e) => setToBeMultiplied(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="number-to-be-multiplied" className="font-inter text-sm text-gray-700">
              Check the box if you want to multiply the price with the number
            </label>
          </div>
        </div>
      </Modal>
    );
  }
);

NumberOptionModal.displayName = 'NumberOptionModal';

export default NumberOptionModal;
